
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'sonner';

export interface Ahavexer {
  id: string;
  uid: string;
  displayName?: string;
  fullName?: string;
  email: string;
  ahavexId: string;
  status?: 'active' | 'pending' | 'suspended';
  createdAt: string;
  businessName?: string;
  phoneNumber?: string;
  address?: string;
}

export interface User {
  id: string;
  uid: string;
  displayName?: string;
  fullName?: string;
  email: string;
  role?: string;
  ahavexId?: string;
  createdAt: string;
  lastLogin?: string;
  phoneNumber?: string;
  address?: string;
}

// Fetch all Ahavexers
export const useAhavexers = () => {
  return useQuery({
    queryKey: ['ahavexers'],
    queryFn: async (): Promise<Ahavexer[]> => {
      try {
        const usersRef = collection(db, 'users');
        // Query for users that have ahavexId field
        const ahavexersQuery = query(usersRef, where('ahavexId', '!=', null));
        const querySnapshot = await getDocs(ahavexersQuery);
        
        return querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            uid: data.uid || doc.id,
            email: data.email || '',
            fullName: data.fullName || '',
            displayName: data.displayName || data.fullName || '',
            ahavexId: data.ahavexId || '',
            status: data.status || 'pending',
            createdAt: formatTimestamp(data.createdAt),
            businessName: data.businessName || '',
            phoneNumber: data.phoneNumber || '',
            address: data.address || ''
          };
        });
      } catch (error) {
        console.error('Error fetching ahavexers:', error);
        throw error;
      }
    },
  });
};

// Fetch all Users (regardless of whether they have ahavexId or not)
export const useAllUsers = () => {
  return useQuery({
    queryKey: ['allUsers'],
    queryFn: async (): Promise<User[]> => {
      try {
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        
        return querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            uid: data.uid || doc.id,
            email: data.email || '',
            fullName: data.fullName || '',
            displayName: data.displayName || data.fullName || '',
            role: data.role || 'customer',
            ahavexId: data.ahavexId || undefined,
            createdAt: formatTimestamp(data.createdAt),
            lastLogin: data.lastLogin ? formatTimestamp(data.lastLogin) : undefined,
            phoneNumber: data.phoneNumber || '',
            address: data.address || ''
          };
        });
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    },
  });
};

// Helper function to safely format timestamps
const formatTimestamp = (timestamp: any): string => {
  if (!timestamp) return new Date().toISOString();
  
  try {
    // Handle Firestore Timestamp objects
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    
    // Handle timestamp objects with seconds and nanoseconds
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toISOString();
    }
    
    // Handle ISO string format
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toISOString();
    }
    
    // Handle numeric timestamps (milliseconds since epoch)
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toISOString();
    }
    
    // Default fallback
    return new Date().toISOString();
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return new Date().toISOString();
  }
};

// Get a single ahavexer by ID
export const useAhavexer = (ahavexerId: string) => {
  return useQuery({
    queryKey: ['ahavexer', ahavexerId],
    queryFn: async (): Promise<Ahavexer | null> => {
      try {
        if (!ahavexerId) return null;
        
        const docRef = doc(db, 'users', ahavexerId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            uid: data.uid || docSnap.id,
            email: data.email || '',
            fullName: data.fullName || '',
            displayName: data.displayName || data.fullName || '',
            ahavexId: data.ahavexId || '',
            status: data.status || 'pending',
            createdAt: data.createdAt ? new Date(data.createdAt).toISOString() : new Date().toISOString(),
            businessName: data.businessName || '',
            phoneNumber: data.phoneNumber || '',
            address: data.address || ''
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching ahavexer:', error);
        throw error;
      }
    },
    enabled: !!ahavexerId,
  });
};

// Update an Ahavexer
export const useUpdateAhavexer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ahavexerId, data }: { ahavexerId: string; data: Partial<Ahavexer> }) => {
      try {
        const ahavexerRef = doc(db, 'users', ahavexerId);
        await updateDoc(ahavexerRef, {
          ...data,
          lastUpdated: new Date().toISOString()
        });
        return { id: ahavexerId, ...data };
      } catch (error) {
        console.error('Error updating ahavexer:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('Ahavexer updated successfully');
      // Invalidate and refetch the ahavexers list
      queryClient.invalidateQueries({ queryKey: ['ahavexers'] });
      // Also invalidate all users
      queryClient.invalidateQueries({ queryKey: ['allUsers'] });
      // Also invalidate dashboard stats since they include ahavexer count
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
    },
    onError: () => {
      toast.error('Failed to update ahavexer');
    }
  });
};
